/**
 * useFiasAIArche - Hook for arche-specific Fias AI integration.
 *
 * Composes useFiasAIChat with panel open/close state.
 * This is the single hook an arche calls to get full Fias AI popup support.
 */

import { useState, useCallback } from 'react';
import { useFiasAIChat } from './useFiasAIChat';
import type { FiasAIChatMessage } from './useFiasAIChat';
import type { ArcheAICapabilities } from '../types';

interface UseFiasAIArcheOptions {
  capabilities: ArcheAICapabilities;
}

interface UseFiasAIArcheReturn {
  // Panel state
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  // Chat state
  messages: FiasAIChatMessage[];
  isStreaming: boolean;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  submit: (message: string) => Promise<void>;
  stop: () => void;
  reset: () => void;
  applyProposal: (proposalId: string) => void;
  rejectProposal: (proposalId: string) => void;
}

export function useFiasAIArche({ capabilities }: UseFiasAIArcheOptions): UseFiasAIArcheReturn {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

  const chat = useFiasAIChat({ capabilities });

  return {
    isPanelOpen,
    openPanel,
    closePanel,
    togglePanel,
    ...chat,
  };
}
